import React, { useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { DefaultButton, mergeStyleSets } from '@fluentui/react';
import { CancelToken } from 'shared/utils/cancelToken';
import { runAction, runShortAction } from 'src/services/utils';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import ButtonTooltip from '@v2/components/base/ButtonTooltip';

import slackImage from 'assets/images/slack-mark.svg';

const classNames = mergeStyleSets({
  slackButton: {
    width: 16,
    height: 16,
  },
  link: {
    display: 'block',
    marginTop: 8,
  }
})

const SlackChannelBrowser = ({ userStore, selectedId, onChange }) => {
  const { isSlackAuthorised, slackChannels } = userStore;
  const cancelTokenRef = useRef();

  const channels = useMemo(() => [...slackChannels]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }))
    .map(({ id, name, is_private }) => ({ key: id, text: `${is_private ? '🔒' : '#󠀠󠀠'} ${name}` }))
    , [slackChannels]);

  useEffect(() => {
    if (isSlackAuthorised) {
      runShortAction(() => userStore.fetchSlackChannels());
    }
    return () => {
      cancelTokenRef.current?.cancel();
    }
  }, []);

  function handleOnChange(_, item) {
    onChange(item.key);
  }

  function handleLoginClick() {
    runAction(async () => {
      cancelTokenRef.current?.cancel();
      cancelTokenRef.current = CancelToken.source();

      await userStore.loginToSlack(cancelTokenRef.current);
    })
  }

  function handleLogoutClick() {
    runShortAction(async () => {
      await userStore.logoutFromSlack();
    });
  }

  return (
    <div>
      {(isSlackAuthorised || channels.length)
        ? <div>
          <SearchableComboBox placeholder="Select Channel"
            selectedKey={selectedId} onChange={handleOnChange} options={channels}
          />
          <ButtonTooltip content="Disconnecting Slack here will stop Slack notifications for the current document only, but will not uninstall the Slack app. Other documents where you have Slack notifications set up will continue to work." styles={{ root: { width: 'auto' } }}>
            <Link className={classNames.link} to="#" onClick={handleLogoutClick}>Disconnect Slack</Link>
          </ButtonTooltip>
        </div>
        :
        <DefaultButton
          iconProps={{ imageProps: { src: slackImage, className: classNames.slackButton } }}
          onClick={handleLoginClick} text="Add to Slack"
        />
      }
    </div>
  )
}

export default inject('userStore')(observer(SlackChannelBrowser));
